import React, {useEffect, useState} from "react";
import {AlfyRadioButtonsGroup} from "../components/create-appointment/AlfyRadioButtonsGroup";
import {Box, Grid, LinearProgress} from "@mui/material";
import {
    alfyToast,
    getNextNthMonthSameDate,
    getShortenDateTimeWithYear,
    getShortenDateWithDayOfWeek
} from "../utils/Utils";
import {AlfyCircularRadioButtonsGroup} from "../components/create-appointment/AlfyCircularRadioButtonsGroup";
import {AlfyButton} from "../components/AlfyButton";
import {useNavigate} from "react-router-dom";
import {CreateAppointmentRequest} from "../models/request/appointment";
import {useCreateAppointment} from "../states/appointment/server";
import dayjs from "dayjs";
import {useNavbar} from "../states/common/client";

const AppointmentCreatePage: React.FC = () => {
    // states
    const [selectedDuration, setSelectedDuration] = useState<string | null>(null)
    const [selectedReminderOption, setSelectedReminderOption] = useState<string | null>(null)
    const [selectedTimeType, setSelectedTimeType] = useState<string | null>(null)
    const [formCompleted, setFormCompleted] = useState<boolean>(false)

    // hooks
    const { setNavbarTitle, reset } = useNavbar()

    // mutations
    const { mutateAsync: createAppointment } = useCreateAppointment();

    // others
    const navigate = useNavigate();

    const arrangeDurationOptions = [
        {value: "1w", label: "7일 안에"},
        {value: "2w", label: "14일 안에"},
        {value: "1m", label: "1달 안에"},
        {value: "2m", label: "2달 안에"},
    ];

    const pollReminderOptions = [
        {value: "6h", label: "6시간 안에"},
        {value: "today", label: "오늘 안에"},
        {value: "tomorrow", label: "내일 안에"},
        {value: "dayAfterTomorrow", label: "모레 안에"},
    ];

    const timeTypes = [
        {value: "MORNING", label: "⛅️", text: "오전"},
        {value: "AFTERNOON", label: "🌞", text: "점심"},
        {value: "EVENING", label: "🌙", text: "저녁"},
    ];

    const getEndDateString = () => {
        switch (selectedDuration) {
            case "1w":
                return getShortenDateWithDayOfWeek(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
            case "2w":
                return getShortenDateWithDayOfWeek(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));
            case "1m":
                return getShortenDateWithDayOfWeek(getNextNthMonthSameDate(new Date(), 1));
            case "2m":
                return getShortenDateWithDayOfWeek(getNextNthMonthSameDate(new Date(), 2));
            default:
        }
    }

    /**
     * 투표 마감 시간 문자열 반환
     * 아무 것도 선택하지 않았으면 null 반환
     * 계산된 투표 마감시간이 현재 시간보다 이전 시간이면 null 반환
     */
    const calculateReminderTime = () => {
        const currentDate = dayjs(new Date());

        if (selectedReminderOption === "6h") {
            const date = currentDate.add(6, 'hour')
                .subtract(3, 'hour')
                .toDate()
            return getShortenDateTimeWithYear(date)
        }
        if (selectedReminderOption === "today") {
            const date = currentDate
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
            if (date.isBefore(currentDate)) {
                return null
            }

            return getShortenDateTimeWithYear(date.toDate())
        }
        if (selectedReminderOption === "tomorrow") {
            const date= currentDate.add(1, 'day')
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
                .toDate()
            return getShortenDateTimeWithYear(date)
        }
        if (selectedReminderOption === "dayAfterTomorrow") {
            const date = currentDate.add(2, 'day')
                .set('hour', 21)
                .set('minute', 0)
                .set('second', 0)
                .toDate()
            return getShortenDateTimeWithYear(date)
        }

        return null
    }

    useEffect(() => {
        reset()
        setNavbarTitle("약속 만들기")
    }, []);

    useEffect(() => {
        if (selectedDuration != null && selectedTimeType != null && selectedReminderOption != null) {
            setFormCompleted(true);
        }
    }, [selectedDuration, selectedTimeType, selectedReminderOption])

    const handleSubmit = async () => {
        if (!formCompleted) {
            alfyToast("약속 생성을 위한 정보를 모두 입력해주세요.");
            return;
        }
        if (selectedDuration == null || selectedTimeType == null || selectedReminderOption == null) {
            alfyToast("약속 생성을 위한 정보를 모두 입력해주세요.");
            return;
        }

        const request: CreateAppointmentRequest = {
            duration: selectedDuration,
            timeType: selectedTimeType,
            reminder: selectedReminderOption,
        }
        createAppointment(request)
            .then((appointment) => {
                navigate(`/appointments/${appointment.id}/poll`, { replace: true });
            })
            .catch(() => {
                alfyToast("약속 생성에 실패했습니다. 잠시 뒤 다시 시도해주세요.")
            })
    }

    return (
        <div className={"fullscreen"}>
            <LinearProgress variant="determinate" value={33} />
            <Box height={"30px"}/>
            <div className="container">
                <span className={"primary-title-text"}>사람들과 약속잡을<br/>대략적인 정보를 알려주세요</span>
                <Box height={"8px"}/>
                <span className={"description-text"}>최대 12명까지 참여할 수 있어요</span>
            </div>
            <Box height={"30px"}/>
            <div className="container">
                <span className={"secondary-title-text"}>약속잡을 기간</span>
                <Box height={"15px"}/>
                <AlfyRadioButtonsGroup
                    options={arrangeDurationOptions}
                    selectedValue={selectedDuration}
                    setSelectedValue={setSelectedDuration}
                />
                {selectedDuration && <p className={"description-text"} style={{marginTop: 0, marginBottom: 0}}>{getShortenDateWithDayOfWeek(new Date())}부터 {getEndDateString()}사이에 약속을 잡아봐요</p>}
            </div>
            <Box height={"40px"}></Box>
            <div className="container">
                <span className={"secondary-title-text"}>만나는 시간대</span>
                <Box height={"7px"}/>
                <span className={"description-text"}>사람들과 만날 대략적인 시간대</span>
                <Box height={"17px"}/>
                <Grid container width={'100%'} justifyContent={'center'}>
                    <AlfyCircularRadioButtonsGroup
                        options={timeTypes}
                        selectedValue={selectedTimeType}
                        setSelectedValue={setSelectedTimeType}
                    />
                </Grid>
            </div>
            <Box height={"40px"}></Box>
            <div className="container">
                <span className={"secondary-title-text"}>투표 마감 시간</span>
                <Box height={"15px"}/>
                <AlfyRadioButtonsGroup
                    options={pollReminderOptions}
                    selectedValue={selectedReminderOption}
                    setSelectedValue={setSelectedReminderOption}
                />
                <Box height={"7px"}/>
                {calculateReminderTime() &&
                  <p className={"description-text"} style={{marginTop: 0, marginBottom: 0}}>{calculateReminderTime()}쯤에 회원님께 메일로 리마인더를 보내드릴게요.</p>}
            </div>
            <Box height={"40px"}></Box>
            <AlfyButton text={"약속 만들기"} onClick={() => {
                handleSubmit();
            }} disabled={!formCompleted}/>
            <Box height={"20px"}/>
        </div>
    );
}

export default AppointmentCreatePage;